import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import LoginPage from "./pages/LoginPage";
import AppLayout from "./pages/AppLayout";
import MaterialsPage from "./pages/MaterialsPage";
import CalculatorPage from "./pages/CalculatorPage";
import DevizPage from "./pages/DevizPage";
import InvoicesPage from "./pages/InvoicesPage";
import AccountsPage from "./pages/AccountsPage";
import VehiclesPage from "./pages/VehiclesPage";
import NotesPage from "./pages/NotesPage";
import { can, firstAllowedPath } from "./lib/perms";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500 font-mono text-sm">
        Se incarca...
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to={firstAllowedPath(user)} replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to={firstAllowedPath(user)} replace />;
  return children;
}

function PermRoute({ perm, children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!can(user, perm)) return <Navigate to={firstAllowedPath(user)} replace />;
  return children;
}

function IndexRedirect() {
  const { user } = useAuth();
  return <Navigate to={firstAllowedPath(user)} replace />;
}

function NoAccess() {
  return (
    <div className="p-10 text-zinc-600" data-testid="no-access-page">
      Contul tau nu are acces la nicio sectiune. Contacteaza administratorul.
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<IndexRedirect />} />
        <Route path="fara-acces" element={<NoAccess />} />
        <Route path="materiale" element={<PermRoute perm="materials"><MaterialsPage /></PermRoute>} />
        <Route path="calculator" element={<PermRoute perm="calculator"><CalculatorPage /></PermRoute>} />
        <Route path="deviz" element={<PermRoute perm="deviz"><DevizPage /></PermRoute>} />
        <Route path="facturi" element={<PermRoute perm="invoices"><InvoicesPage /></PermRoute>} />
        <Route path="vehicule" element={<PermRoute perm="vehicles"><VehiclesPage /></PermRoute>} />
        <Route path="notite" element={<PermRoute perm="notes"><NotesPage /></PermRoute>} />
        <Route
          path="conturi"
          element={
            <AdminOnly>
              <AccountsPage />
            </AdminOnly>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
